import { useMemo, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormField } from '../shared/FormField';
import { NativeSelect } from '../shared/NativeSelect';
import { SearchInput } from '../shared/SearchInput';
import { RegistrarPagoModal } from '../shared/RegistrarPagoModal';
import { ArbolCobrosPendientes, type RecordatorioCobro } from './ArbolCobrosPendientes';
import WhatsAppCobroPendienteModal from '@/components/WhatsAppCobroPendienteModal';
import { useArbolCobros } from '../../hooks/useArbolCobros';
import { useCobrosPendientes } from '../../hooks/useCobrosPendientes';
import { useCajas } from '../../hooks/useCajas';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { SECTION_LABEL_CLASS } from '../../constants/ui';
import { formatAmount } from '../../utils/formatters';
import { MONEDAS, buscarNodo, clientesDelNodo, filtrarArbol } from '../../utils/agrupadores';
import type { CobroPendiente, ItemCobroPendiente, Moneda, NodoCobros, PagoPayload } from '../../types';

const DEFAULTS = { moneda: 'todas' as Moneda | 'todas', search: '' };

/** Todos los clientes del agrupador, en el select de cliente. */
const TODOS = '';

/**
 * Cobros pendientes por agrupador: de una escritura se cobra un agrupador
 * entero, solo los ítems de un cliente dentro de él, o un ítem. Los otros
 * trámites, sin agrupador, van en un nodo aparte y se cobran de a uno.
 */
export function CobrosPorAgrupador() {
  const { successAlert, errorAlert } = useAlert();
  const { arbol, isLoading, cobrarAgrupador, cobrarItem, isCobrando } = useArbolCobros();
  const { cobrosPendientes, isLoading: isLoadingCobros, registrarCobro, isRegistrando } = useCobrosPendientes();
  const { cajas } = useCajas();
  const { canCreateContabilidad, canSendWhatsApp } = useAuthorization();

  const [moneda, setMoneda] = useState(DEFAULTS.moneda);
  const [search, setSearch] = useState(DEFAULTS.search);
  const [cobrandoNodo, setCobrandoNodo] = useState<{ nodo: NodoCobros; moneda: Moneda; cliente: string } | null>(null);
  const [cobrandoItem, setCobrandoItem] = useState<ItemCobroPendiente | null>(null);
  const [cobrandoOtro, setCobrandoOtro] = useState<CobroPendiente | null>(null);
  const [recordatorio, setRecordatorio] = useState<RecordatorioCobro | null>(null);

  // "Sin agrupar" va al final, como un nodo más.
  const nodos = useMemo(
    () => (arbol ? filtrarArbol([...arbol.agrupadores, arbol.sinAgrupar], { moneda, search }) : []),
    [arbol, moneda, search],
  );

  const otrosTramites = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cobrosPendientes.filter((c) =>
      c.origen === 'otro_tramite'
      && c.saldoRestante > 0
      && (moneda === 'todas' || c.moneda === moneda)
      && (!q
        || c.clienteNombre.toLowerCase().includes(q)
        || c.itemNombre.toLowerCase().includes(q)
        || (c.otroTramiteId ?? '').toLowerCase().includes(q)
        || 'otros trámites'.includes(q)));
  }, [cobrosPendientes, moneda, search]);

  const saldoTotal = useMemo(() => {
    const raices = arbol ? [...arbol.agrupadores, arbol.sinAgrupar] : [];
    const otros = cobrosPendientes.filter((c) => c.origen === 'otro_tramite' && c.saldoRestante > 0);
    return MONEDAS.map((m) => ({
      moneda: m,
      saldo: raices.reduce((acc, n) => acc + n.totales[m].saldo, 0)
        + otros.filter((c) => c.moneda === m).reduce((acc, c) => acc + c.saldoRestante, 0),
    }))
      .filter((t) => t.saldo > 0 && (moneda === 'todas' || t.moneda === moneda));
  }, [arbol, cobrosPendientes, moneda]);

  // Los clientes salen del nodo sin filtrar: con búsqueda faltan ítems.
  const clientes = useMemo(() => {
    if (!arbol || !cobrandoNodo || cobrandoNodo.nodo.id === null) return [];
    const nodo = buscarNodo(arbol.agrupadores, cobrandoNodo.nodo.id) ?? cobrandoNodo.nodo;
    return clientesDelNodo(nodo, cobrandoNodo.moneda);
  }, [arbol, cobrandoNodo]);

  async function handleSubmitCobro(payload: PagoPayload) {
    if (!cobrandoNodo || cobrandoNodo.nodo.id === null) return;
    try {
      const { imputaciones } = await cobrarAgrupador({
        id: cobrandoNodo.nodo.id,
        moneda: cobrandoNodo.moneda,
        payload,
        cliente: cobrandoNodo.cliente || undefined,
      });
      successAlert({
        title: 'Cobro registrado',
        message: `${cobrandoNodo.nodo.nombre}: se imputó a ${imputaciones.length} ${imputaciones.length === 1 ? 'ítem' : 'ítems'}, del más viejo al más nuevo.`,
      });
      setCobrandoNodo(null);
    } catch (err) {
      errorAlert({ title: 'Error al registrar cobro', exception: err });
    }
  }

  async function handleSubmitCobroItem(payload: PagoPayload) {
    if (!cobrandoItem) return;
    try {
      await cobrarItem({ id: cobrandoItem.id, payload });
      successAlert({ title: 'Cobro registrado', message: `${cobrandoItem.itemNombre} (trámite #${cobrandoItem.tramiteId}).` });
      setCobrandoItem(null);
    } catch (err) {
      errorAlert({ title: 'Error al registrar cobro', exception: err });
    }
  }

  async function handleSubmitCobroOtro(payload: PagoPayload) {
    if (!cobrandoOtro) return;
    try {
      await registrarCobro({ cobro: cobrandoOtro, payload });
      successAlert({ title: 'Cobro registrado', message: `${cobrandoOtro.itemNombre} actualizado correctamente.` });
      setCobrandoOtro(null);
    } catch (err) {
      errorAlert({ title: 'Error al registrar cobro', exception: err });
    }
  }

  if (isLoading || isLoadingCobros) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  const totalesNodo = cobrandoNodo ? cobrandoNodo.nodo.totales[cobrandoNodo.moneda] : null;
  const cliente = cobrandoNodo?.cliente ? clientes.find((c) => c.clave === cobrandoNodo.cliente) : undefined;
  const resumen = cliente
    ? { total: cliente.total, pagado: cliente.cobrado, saldo: cliente.saldo, cantidad: cliente.cantidad }
    : totalesNodo;
  const mezclaClientes = !cliente && clientes.length > 1;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-gray-100">
        <AlertCircle className="size-4 text-muted-foreground" />
        <p className={SECTION_LABEL_CLASS}>Gestión de Cobros Pendientes</p>
        {saldoTotal.length > 0 && (
          <p className="ml-auto text-sm text-muted-foreground">
            Saldo total:{' '}
            {saldoTotal.map((t, i) => (
              <span key={t.moneda} className="font-semibold tabular-nums text-gray-900">
                {i > 0 && ' · '}{t.moneda} $ {formatAmount(t.saldo, t.moneda)}
              </span>
            ))}
          </p>
        )}
      </div>

      <div className="px-5 py-4 border-b border-gray-100">
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex gap-3 items-end flex-wrap">
            <FormField label="Moneda" className="w-36">
              <NativeSelect value={moneda} onChange={(e) => setMoneda(e.target.value as Moneda | 'todas')}>
                <option value="todas">Todas</option>
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </NativeSelect>
            </FormField>
            <FormField label="Buscar" className="flex-1 min-w-48">
              <SearchInput
                placeholder="Buscar agrupador, ítem, cliente o trámite..."
                value={search}
                onChange={setSearch}
              />
            </FormField>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setMoneda(DEFAULTS.moneda); setSearch(DEFAULTS.search); }}
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="size-3.5" /> Limpiar
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Cobrar un agrupador incluye sus subagrupadores e imputa del ítem más viejo al más nuevo; si tiene ítems de
            más de un cliente, se cobra el saldo completo o se elige un cliente. También se puede cobrar un ítem solo.
            El recordatorio de WhatsApp es por el saldo de todo el trámite.
          </p>
        </div>
      </div>

      <ArbolCobrosPendientes
        nodos={nodos}
        otrosTramites={otrosTramites}
        moneda={moneda}
        expandirTodo={search.trim() !== ''}
        canCobrar={canCreateContabilidad}
        canWhatsApp={canSendWhatsApp}
        disabled={isCobrando || isRegistrando}
        onCobrarNodo={(nodo, m) => setCobrandoNodo({ nodo, moneda: m, cliente: TODOS })}
        onCobrarItem={setCobrandoItem}
        onCobrarOtroTramite={setCobrandoOtro}
        onWhatsApp={setRecordatorio}
      />

      {cobrandoNodo && resumen && (
        <RegistrarPagoModal
          open={true}
          titulo="Cobrar agrupador"
          cajaLabel="Caja Destino"
          itemNombre={cliente ? `${cobrandoNodo.nodo.nombre} — ${cliente.nombre}` : cobrandoNodo.nodo.nombre}
          clienteNombre={`${resumen.cantidad} ${resumen.cantidad === 1 ? 'ítem pendiente' : 'ítems pendientes'}`
            + (mezclaClientes ? ` de ${clientes.length} clientes` : ' · se imputa del más viejo al más nuevo')}
          total={resumen.total}
          totalMovido={resumen.pagado}
          saldoRestante={resumen.saldo}
          moneda={cobrandoNodo.moneda}
          cajas={cajas}
          isSubmitting={isCobrando}
          montoBloqueado={mezclaClientes ? 'Mezcla clientes: elegí un cliente para cobrar una parte.' : undefined}
          onSubmit={handleSubmitCobro}
          onClose={() => setCobrandoNodo(null)}
        >
          {clientes.length > 1 && (
            <FormField label="Cliente">
              <NativeSelect
                value={cobrandoNodo.cliente}
                onChange={(e) => setCobrandoNodo({ ...cobrandoNodo, cliente: e.target.value })}
              >
                <option value={TODOS}>Todos los clientes ({clientes.length})</option>
                {clientes.map((c) => (
                  <option key={c.clave} value={c.clave}>
                    {c.nombre} — {c.cantidad} {c.cantidad === 1 ? 'ítem' : 'ítems'} · {cobrandoNodo.moneda} $ {formatAmount(c.saldo, cobrandoNodo.moneda)}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          )}
        </RegistrarPagoModal>
      )}

      {cobrandoItem && (
        <RegistrarPagoModal
          open={true}
          titulo="Cobrar ítem"
          cajaLabel="Caja Destino"
          itemNombre={cobrandoItem.tramitePropiedad
            ? `${cobrandoItem.tramitePropiedad} - ${cobrandoItem.itemNombre}`
            : cobrandoItem.itemNombre}
          clienteNombre={`${cobrandoItem.clienteNombre ? `${cobrandoItem.clienteNombre} · ` : ''}Trámite #${cobrandoItem.tramiteId}`}
          total={cobrandoItem.total}
          totalMovido={cobrandoItem.totalCobrado}
          saldoRestante={cobrandoItem.saldoRestante}
          moneda={cobrandoItem.moneda}
          cajas={cajas}
          isSubmitting={isCobrando}
          onSubmit={handleSubmitCobroItem}
          onClose={() => setCobrandoItem(null)}
        />
      )}

      {cobrandoOtro && (
        <RegistrarPagoModal
          open={true}
          titulo="Registrar Cobro"
          cajaLabel="Caja Destino"
          itemNombre={cobrandoOtro.itemNombre}
          clienteNombre={cobrandoOtro.clienteNombre}
          total={cobrandoOtro.total}
          totalMovido={cobrandoOtro.totalCobrado}
          saldoRestante={cobrandoOtro.saldoRestante}
          moneda={cobrandoOtro.moneda}
          cajas={cajas}
          isSubmitting={isRegistrando}
          onSubmit={handleSubmitCobroOtro}
          onClose={() => setCobrandoOtro(null)}
        />
      )}

      {recordatorio && (
        <WhatsAppCobroPendienteModal
          open={true}
          onOpenChange={(isOpen) => {
            if (!isOpen) setRecordatorio(null);
          }}
          cobro={recordatorio}
        />
      )}
    </>
  );
}
